import type { ButtonHTMLAttributes, JSX, ReactNode } from 'react';
import { Icon, type IconName } from './Icon';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  /**
   * primary — светлая кнопка на тёмном, одно действие на экран;
   * secondary — обычное действие;
   * ghost — действие в плотном ряду, без заливки;
   * danger — необратимое действие.
   *
   * Всё, что удаляет — узел, площадку, слой, рамку, связь, канал, правило,
   * учётные данные, поле, раскладку, — берёт `danger`, и не важно, стоит ли
   * кнопка отдельно или в строке таблицы. Цвет тут не про важность, а про
   * последствие: одинаковое действие должно выглядеть одинаково на всех
   * экранах, иначе красное на карте и серое в таблице читаются как разные
   * по опасности, хотя оба удаляют навсегда.
   */
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md';
  /**
   * Иконка перед подписью. Всегда слева и всегда с подписью: иконка здесь —
   * опора для глаза при беглом просмотре, а не замена словам.
   */
  icon?: IconName;
  /**
   * Только классы размещения — отступы, выравнивание в потоке.
   * Оформление задаётся `variant` и `size`: иначе кнопки разъедутся
   * по экранам, а §12 требует единого вида.
   */
  className?: string;
  /**
   * Подпись. Может отсутствовать только у кнопки в плотном ряду, где на текст
   * нет места, — и тогда обязателен `aria-label`: иконка помечена
   * `aria-hidden`, и без имени кнопка для скринридера безымянна.
   */
  children?: ReactNode;
}

/**
 * Кнопка по макету. Цвет здесь не декоративный: по §12 акцентных кнопок
 * «для красоты» нет, а красный означает необратимость действия, а не важность.
 */
export function Button({
  variant = 'secondary',
  size = 'md',
  className = '',
  icon,
  // Браузер считает кнопку без `type` внутри формы отправляющей. Из-за этого
  // «Отмена» и «Добавить поле» отправляли бы форму вместо своего действия,
  // причём молча и только в некоторых местах. Отправка требует явного
  // `type="submit"` — так её видно в коде.
  type = 'button',
  children,
  ...button
}: ButtonProps): JSX.Element {
  const base =
    'inline-flex items-center justify-center gap-1.5 rounded-sm font-medium whitespace-nowrap ' +
    'disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none';

  const sizes = {
    sm: 'h-6 px-2.5 text-sm',
    md: 'h-9 px-3 text-base',
  };

  const variants = {
    primary: 'bg-control-primary text-control-primary-fg hover:opacity-90',
    secondary: 'bg-control text-fg hover:brightness-110',
    ghost: 'border border-line-strong text-muted hover:text-fg',
    danger: 'border border-down-line bg-down-bg text-down-fg hover:brightness-110',
  };

  return (
    <button
      {...button}
      type={type}
      className={[base, sizes[size], variants[variant], className].join(' ')}
    >
      {icon ? <Icon name={icon} /> : null}
      {children}
    </button>
  );
}
